// Wave AW.3-React Wave 3 (2026-05-16) — 외부 바이어 내 입찰 화면
// API: GET /buyer-app/me/bids → { success, data: { bids: [...] } }
// 응답의 auction.myBid.status 로 client-side 필터 (탭: 진행중/낙찰/미낙찰)
//
// 단일 source 채택 (옵션 1) — /me/wins 미사용:
//   - winner+pending_selection 윈도우가 어디에도 안 잡히는 누락 차단
//   - 'cancelled' 는 노출 제외 (관리자 무효 처리)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerCard from './AuctionBuyerCard';
import AuctionBuyerLayout from './AuctionBuyerLayout';

var TABS = [
  { key: 'active',   label: '진행중' },
  { key: 'winner',   label: '낙찰' },
  { key: 'rejected', label: '미낙찰' },
];

function AuctionBuyerBidsList() {
  var navigate = useNavigate();
  var [tab, setTab] = useState('active');
  var [allBids, setAllBids] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/me/bids', {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data) {
          setAllBids(d.data.bids || []);
        } else {
          setError((d && d.error) || '목록 로드 실패');
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError('네트워크 오류');
        setLoading(false);
      });
  }, [navigate]);

  useEffect(function() { load(); }, [load]);

  // client-side 필터 — 한 번의 fetch 로 3개 탭 모두 분류.
  var filtered = allBids.filter(function(a) {
    var s = a && a.myBid && a.myBid.status;
    return s === tab;
  });

  var counts = {
    active:   allBids.filter(function(a){ return a && a.myBid && a.myBid.status === 'active'; }).length,
    winner:   allBids.filter(function(a){ return a && a.myBid && a.myBid.status === 'winner'; }).length,
    rejected: allBids.filter(function(a){ return a && a.myBid && a.myBid.status === 'rejected'; }).length,
  };

  var emptyMessage = (function() {
    if (tab === 'active')   return '진행중인 입찰이 없습니다.';
    if (tab === 'winner')   return '낙찰된 차량이 없습니다.';
    if (tab === 'rejected') return '미낙찰 입찰이 없습니다.';
    return '결과 없음';
  })();

  return (
    <AuctionBuyerLayout activeKey="bids">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 80px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>내 입찰</h2>
        </div>

        {/* 탭 */}
        <div style={{
          display: 'flex', gap: 4,
          borderBottom: '1.5px solid #e2e8f0',
          marginBottom: 20,
        }}>
          {TABS.map(function(t) {
            var active = tab === t.key;
            var count = counts[t.key];
            return (
              <button key={t.key} type="button"
                onClick={function(){ setTab(t.key); }}
                style={{
                  padding: '12px 18px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: active ? '2.5px solid #1B3A5C' : '2.5px solid transparent',
                  fontSize: 14, fontWeight: active ? 700 : 600,
                  color: active ? '#1B3A5C' : '#64748b',
                  cursor: 'pointer',
                  fontFamily: "'Pretendard',sans-serif",
                  marginBottom: '-1.5px',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <span>{t.label}</span>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  background: active ? '#1B3A5C' : '#e2e8f0',
                  color: active ? 'white' : '#64748b',
                  padding: '2px 8px', borderRadius: 10,
                  minWidth: 18, textAlign: 'center',
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* 본문 */}
        {loading && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            불러오는 중...
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{error}</div>
            <button type="button" onClick={load}
              style={{
                padding: '8px 16px',
                background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8,
                fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer',
                fontFamily: "'Pretendard',sans-serif",
              }}>
              다시 시도
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div style={{ padding: '80px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            {emptyMessage}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
          }}>
            {filtered.map(function(a) {
              return (
                <AuctionBuyerCard
                  key={a.id}
                  auction={a}
                  onClick={function(){ navigate('/buyer/auctions/' + a.id); }}
                />
              );
            })}
          </div>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerBidsList;
